import { supabase } from "@/lib/supabase";

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

export async function optimizeImage(file: File, maxDim = 1800): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  try {
    const img = await loadImage(file);
    const longEdge = Math.max(img.width, img.height);
    if (longEdge <= maxDim) return file;
    const scale = maxDim / longEdge;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const isPng = file.type === "image/png";
    const type = isPng ? "image/png" : "image/jpeg";
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("no blob"))), type, isPng ? undefined : 0.85)
    );
    const ext = isPng ? "png" : "jpg";
    return new File([blob], file.name.replace(/\.[^.]+$/, `.${ext}`), { type });
  } catch {
    return file;
  }
}

function sanitizeSegment(segment: string): string {
  return segment
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "-")
    .replace(/^-+|-+$/g, "");
}

export async function uploadImage(
  file: File,
  folder = "general",
): Promise<{ url: string; mediaId?: string }> {
  const optimized = await optimizeImage(file);
  const ext = optimized.name.split(".").pop()?.toLowerCase() || "jpg";
  const safeFolder = sanitizeSegment(folder) || "general";
  const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const path = `${safeFolder}/${safeName}`;

  const { error } = await supabase.storage.from("media").upload(path, optimized, {
    cacheControl: "3600",
    upsert: false,
  });
  if (error) throw error;

  const { data: urlData } = supabase.storage.from("media").getPublicUrl(path);
  const url = urlData.publicUrl;

  let mediaId: string | undefined;
  const { data: mediaRow } = await supabase
    .from("media")
    .insert({
      url,
      nombre: file.name,
      tipo: file.type,
      tamano: optimized.size,
      bucket: "media",
      ruta: path,
    })
    .select("id")
    .maybeSingle();
  mediaId = mediaRow?.id;

  return { url, mediaId };
}

export async function deleteImageByUrl(url: string) {
  const path = url.split("/media/")[1];
  if (path) {
    await supabase.storage.from("media").remove([decodeURIComponent(path)]);
  }
}